import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import leadsWebApi from '../services/leads-web';
import campagnesMediasApi from '../services/campagnes-medias';
import usersApi from '../services/users';
import concessionsApi from '../services/concessions';
import { referentiels as referentielsApi } from '../services/referentiels';
import sujetsApi from '../services/sujets';
import InlineStatusSelect from './InlineStatusSelect';
import LeadsFunnel from './LeadsFunnel';
import LeadsCrm360Import from './LeadsCrm360Import';
import './LeadsView.css';

function LeadsView({ user, editMode, canDelete = false, canModify = false, setHeaderAction }) {
  const [leads, setLeads] = useState([]);
  const [vendeurs, setVendeurs] = useState([]);
  const [concessionsList, setConcessionsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterVendeurId, setFilterVendeurId] = useState('');
  const [filterConcession, setFilterConcession] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterNonAssigne, setFilterNonAssigne] = useState(false);
  const [sourcesMapping, setSourcesMapping] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [commentaires, setCommentaires] = useState([]);
  const [newCommentaire, setNewCommentaire] = useState('');
  const [sortCol, setSortCol] = useState('date_reception');
  const [sortDir, setSortDir] = useState('desc');
  const [archivedLeads, setArchivedLeads] = useState([]);
  const [confirmCrm360LeadId, setConfirmCrm360LeadId] = useState(null);
  const [crm360NumLead, setCrm360NumLead] = useState('');
  // Modale "Transformer en demande client"
  // { lead, sujet, responsable_id, fields: { nom, prenom, telephone, email, marque, modele, immatriculation, vin, bon_de_commande, commentaire_initial } }
  const [transformModal, setTransformModal] = useState(null);
  const [transformSujets, setTransformSujets] = useState([]);
  const [transformResponsables, setTransformResponsables] = useState([]);
  const [transformSaving, setTransformSaving] = useState(false);
  // Modale e-mail Outlook à l'affectation (leads SOGUAVASOREC uniquement)
  const [emailPrompt, setEmailPrompt] = useState(null); // { lead, vendeur }

  // ── Mode édition en masse ──
  // bulkMode actif pour super-admin (toutes actions) OU tout user avec peut_modifier / peut_supprimer sur 'leads'
  //  - menu « Action en masse » (statut/vendeur/service/concession) : super-admin OU peut_modifier
  //  - bouton « Supprimer la sélection » : super-admin OU peut_supprimer
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const isSuperAdmin = user?.role === 'super-admin';
  const isAdmin = isSuperAdmin || user?.role === 'admin';
  const isPrestataire = user?.role === 'prestataire';
  const bulkMode = editMode && (isSuperAdmin || canDelete || canModify);

  // Accès au sous-onglet « Suivi collaborateurs » :
  //  - super-admin : toujours OK (voit tous les collaborateurs + bloc Non assigné)
  //  - users.is_manager = true : voit ses subordonnés directs (manager_id côté backend)
  const canViewSuivi = isSuperAdmin || Boolean(user?.is_manager);

  // Liste des vendeurs configurables (avec leur flag absent) — utilisée par
  // l'onglet Configuration ET par les blocs « Vendeurs absents » qui apparaissent
  // en haut des onglets VN / VO pour rappeler à l'admin/manager qu'un vendeur
  // est absent.
  const [teamConfig, setTeamConfig] = useState([]);
  const [absentSaving, setAbsentSaving] = useState({});
  const reloadTeamConfig = useCallback(async () => {
    if (!canViewSuivi) return;
    try {
      const r = await leadsWebApi.getConfigTeam();
      setTeamConfig(r.data?.users || []);
    } catch (err) {
      // 403 attendu si l'utilisateur n'est ni super-admin ni manager — silencieux.
    }
  }, [canViewSuivi]);
  useEffect(() => { reloadTeamConfig(); }, [reloadTeamConfig]);
  // Note : le useEffect qui reload la liste d'absents au changement d'onglet
  // VN/VO est placé plus bas dans le composant, après la déclaration de
  // `activeTab` (sinon TDZ : ReferenceError "Cannot access 'activeTab' before
  // initialization").

  const markPresent = async (u) => {
    setAbsentSaving(s => ({ ...s, [u.id]: true }));
    try {
      await leadsWebApi.setVendeurAbsent(u.id, false);
      setTeamConfig(arr => arr.map(x => x.id === u.id ? { ...x, absent: false } : x));
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setAbsentSaving(s => ({ ...s, [u.id]: false }));
    }
  };

  // Bloc « Vendeurs absents » à afficher en haut des onglets VN / VO. N'apparaît
  // qu'aux managers/super-admins (canViewSuivi) et que s'il y a au moins un
  // vendeur absent pour ce type.
  const renderAbsentsBlock = (type) => {
    if (!canViewSuivi) return null;
    const absents = teamConfig.filter(u => u.absent && (u.types_leads_web || []).includes(type));
    if (absents.length === 0) return null;
    return (
      <div style={{
        marginBottom: 14, padding: '10px 14px', borderRadius: 8,
        background: 'var(--color-error-light)',
        border: '1px solid var(--color-error)',
        color: 'var(--color-error)',
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          ⚠️ {absents.length} vendeur{absents.length > 1 ? 's' : ''} {type} actuellement absent{absents.length > 1 ? 's' : ''}
          <span style={{ fontWeight: 400, marginLeft: 6 }}>
            — n'oubliez pas de remettre « Présent » à son retour.
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {absents.map(u => (
            <div key={u.id} style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '4px 6px 4px 12px', borderRadius: 16,
              background: 'var(--bg-surface)',
              border: '1px solid var(--color-error)',
              color: 'var(--text-primary)', fontSize: 13,
            }}>
              <span><strong>{u.nom}</strong> {u.prenom}{u.concession_nom ? ` · ${u.concession_nom}` : ''}</span>
              <button
                onClick={() => markPresent(u)}
                disabled={!!absentSaving[u.id]}
                style={{
                  padding: '3px 12px', fontSize: 12, fontWeight: 600,
                  borderRadius: 12, border: '1px solid var(--color-success)',
                  background: 'var(--color-success-light)', color: 'light-dark(var(--color-success), var(--color-success-dark))',
                  cursor: absentSaving[u.id] ? 'wait' : 'pointer',
                  opacity: absentSaving[u.id] ? 0.6 : 1,
                }}
                title="Marquer ce vendeur comme présent"
              >
                ✓ Présent
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Onglets ──────────────────────────────────────────────────────────────
  const SERVICES = [
    { code: 'VN',      label: '🚗 VN' },
    { code: 'VO',      label: '🚙 VO' },
    { code: 'ATELIER', label: '🔧 Atelier' },
    { code: 'PIECES',  label: '⚙️ Pièces' },
    { code: 'AUTRE',   label: '📋 Autres' },
  ];
  const serviceCodes = SERVICES.map(s => s.code);
  const [activeTab, setActiveTab]     = useState(isPrestataire ? 'dashboard' : 'VN');

  // Recharger la liste d'absents quand on revient sur VN ou VO depuis l'onglet
  // Configuration (où l'on peut avoir modifié le flag absent).
  useEffect(() => {
    if (activeTab === 'VN' || activeTab === 'VO') reloadTeamConfig();
  }, [activeTab, reloadTeamConfig]);

  // ── Tableau de bord par source/jour ─────────────────────────────────────
  const [dashMois,        setDashMois]        = useState(new Date().getMonth() + 1);
  const [dashAnnee,       setDashAnnee]       = useState(new Date().getFullYear());
  const [dashConcession,  setDashConcession]  = useState(user?.concession_id ? String(user.concession_id) : '');
  const [dashService,     setDashService]     = useState('');
  const [dashCampagne,    setDashCampagne]    = useState('');
  const [dashCampagnesList, setDashCampagnesList] = useState([]);
  const [dashStats,       setDashStats]       = useState([]);
  const [dashLoading,     setDashLoading]     = useState(false);

  // Statuts chargés dynamiquement depuis le référentiel (admin > Référentiels > Statuts Leads Web)
  const [allStatuts, setAllStatuts] = useState([]);

  // Statuts applicables à l'onglet service actif — pour filtres toolbar + bulk select.
  // Réactif à la concession effective : une concession standard ne voit jamais les
  // statuts de suivi interne (et inversement). Concession effective = celle choisie
  // dans le filtre (`filterConcession`) ou celle de l'utilisateur lié ; sinon (« Toutes
  // les concessions », super-admin) on affiche l'union.
  const statutsForActiveTab = useMemo(() => {
    if (!serviceCodes.includes(activeTab)) return [];
    const cid = filterConcession || (user?.concession_id ? String(user.concession_id) : '');
    let interne = null; // null = toutes concessions / inconnu → union
    if (cid) {
      const c = concessionsList.find(x => String(x.id) === String(cid));
      if (c) interne = !!c.suivi_lead_interne;
      else {
        // Repli (ex. opérateur sans concessionsList chargée) : déduire d'un lead chargé.
        const lead = leads.find(l => String(l.concession_id) === String(cid));
        if (lead) interne = !!lead.concession_suivi_interne;
      }
    }
    return allStatuts.filter(s => {
      if (!Array.isArray(s.services) || !s.services.includes(activeTab)) return false;
      if (interne === null) return true;
      return interne ? (s.suivi_interne || s.code === 'a_contacter') : !s.suivi_interne;
    });
  }, [allStatuts, activeTab, filterConcession, concessionsList, leads, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Construit les options InlineStatusSelect pour un lead donné, en tenant compte de
  // son service et en conservant les valeurs legacy comme option grise "non-officielle".
  const buildStatutOptions = (lead) => {
    const service = lead.service_concession || 'VN';
    // Concession en suivi interne : jeu de statuts étendu (codes `suivi_interne`)
    // qui remplace le flux CRM360 ; on garde 'a_contacter' (état initial des leads
    // entrants) et on masque CRM360. Concession standard : comportement inchangé
    // (jamais les statuts de suivi interne).
    const interne = !!lead.concession_suivi_interne;
    const applicable = allStatuts.filter(s => {
      if (!Array.isArray(s.services) || !s.services.includes(service)) return false;
      return interne ? (s.suivi_interne || s.code === 'a_contacter') : !s.suivi_interne;
    });
    const opts = applicable.map(s => ({ value: s.code, label: s.nom, color: s.couleur }));
    const currentCode = lead.statut;
    if (currentCode && !opts.find(o => o.value === currentCode)) {
      // Valeur legacy (leads historiques 'nouveau', 'contacte', 'rdv', 'devis'...) :
      // affichée en gris, reste sélectionnable mais disparaît quand l'user change.
      opts.unshift({ value: currentCode, label: currentCode, color: '#94a3b8' });
    }
    return opts;
  };

  useEffect(() => {
    if (isPrestataire) {
      loadConcessions();
      return;
    }
    loadVendeurs();
    if (['super-admin', 'admin', 'teleoperateur'].includes(user?.role)) loadConcessions();
    referentielsApi.getSourcesMapping().then(r => setSourcesMapping(r.data.sources_mapping || [])).catch(() => {});
    referentielsApi.getStatutsLeadsWeb({ actif: '1' })
      .then(r => setAllStatuts(r.data.statuts_leads_web || []))
      .catch(() => {});
    // Pré-chargement pour la modale "Transformer en demande client"
    sujetsApi.getAll().then(r => setTransformSujets(r.data.sujets || [])).catch(() => {});
    referentielsApi.getResponsables().then(r => setTransformResponsables(r.data.responsables || [])).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    if (isPrestataire) return;
    loadData();
  }, [filterStatut, filterVendeurId, filterConcession]); // eslint-disable-line

  // SSE : synchronisation temps réel multi-utilisateurs.
  //
  // Stratégie « merge idempotent par updated_at » (depuis 2026-05-19) :
  //   - Toute action locale (POST/PUT/DELETE) met d'abord à jour le state local
  //     avec la réponse serveur enrichie (incluant le nouvel updated_at).
  //   - L'event SSE qui arrive ensuite porte le MÊME updated_at → notre filtre
  //     l'ignore naturellement (pas d'écrasement, pas de flicker).
  //   - Un event SSE déclenché par un AUTRE utilisateur a un updated_at plus
  //     récent que la version locale → on l'applique. Ainsi les modifications
  //     concurrentes de plusieurs utilisateurs ne se masquent jamais.
  //
  // Pas de mécanisme « skipSSE » : il était cassé en multi-update concurrent
  // (un seul flag booléen partagé ignorait à tort des events légitimes
  // provenant d'un autre utilisateur).
  const loadDataRef = useRef(null);
  // Badge UX éphémère : « Modifié par X il y a Ns » sur les modifs externes.
  const [recentRemoteEdit, setRecentRemoteEdit] = useState(null); // { who, at }
  const recentRemoteEditTimer = useRef(null);
  const flashRemoteEdit = (modifiedBy) => {
    if (!modifiedBy || modifiedBy.id === user?.id) return;
    setRecentRemoteEdit({
      who: [modifiedBy.prenom, modifiedBy.nom].filter(Boolean).join(' ') || modifiedBy.username || '?',
      at: new Date(),
    });
    if (recentRemoteEditTimer.current) clearTimeout(recentRemoteEditTimer.current);
    recentRemoteEditTimer.current = setTimeout(() => setRecentRemoteEdit(null), 5000);
  };
  // Helper : compare deux updated_at (ISO strings). Renvoie true si `a` est
  // strictement plus récent que `b`. Robuste aux undefined côté local.
  const isNewerThan = (a, b) => {
    if (!b) return true;
    if (!a) return false;
    return new Date(a).getTime() > new Date(b).getTime();
  };
  useEffect(() => {
    if (isPrestataire) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const evtSource = new EventSource(`/api/planning/events?token=${token}`);

    evtSource.addEventListener('leads-updated', (event) => {
      try {
        const data = JSON.parse(event.data || '{}');
        if (!data.lead) return;
        flashRemoteEdit(data._modifiedBy);
        setLeads(prev => {
          const idx = prev.findIndex(l => l.id === data.lead.id);
          if (idx === -1) return prev; // lead pas dans la vue → on ne le ré-injecte pas
          if (!isNewerThan(data.lead.updated_at, prev[idx].updated_at)) return prev;
          // Si le lead vient de passer en statut 'archive' (cas où un autre
          // utilisateur l'archive), on le retire de la vue active.
          if (data.lead.statut === 'archive') {
            return prev.filter(l => l.id !== data.lead.id);
          }
          const next = prev.slice();
          next[idx] = { ...prev[idx], ...data.lead };
          return next;
        });
      } catch {}
    });
    evtSource.addEventListener('leads-created', (event) => {
      try {
        const data = JSON.parse(event.data || '{}');
        if (!data.lead) return;
        // Filet de sécurité côté client : un opérateur rattaché à une concession
        // ne doit jamais voir apparaître un lead d'une autre concession.
        if (user?.role === 'operateur' && user?.concession_id
            && Number(data.lead.concession_id) !== Number(user.concession_id)) {
          return;
        }
        flashRemoteEdit(data._modifiedBy);
        setLeads(prev => {
          if (prev.some(l => l.id === data.lead.id)) return prev; // idempotent
          return [data.lead, ...prev];
        });
      } catch {}
    });
    evtSource.addEventListener('leads-deleted', (event) => {
      try {
        const data = JSON.parse(event.data || '{}');
        if (data.id) {
          flashRemoteEdit(data._modifiedBy);
          setLeads(prev => prev.filter(l => l.id !== data.id));
        }
      } catch {}
    });
    evtSource.addEventListener('leads-bulk', () => {
      // Une action en masse touche potentiellement beaucoup de lignes — on
      // refetch tout, c'est le seul moyen propre de garantir l'état correct.
      loadDataRef.current?.();
    });

    return () => evtSource.close();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (isPrestataire) return;
    if (activeTab === 'archives') loadArchives();
    setSelectedIds(new Set());
    setBulkAction('');
    setBulkValue('');
  }, [activeTab]); // eslint-disable-line

  useEffect(() => {
    if (!editMode) {
      setSelectedIds(new Set());
      setBulkAction('');
      setBulkValue('');
    }
  }, [editMode]);

  const loadDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const r = await leadsWebApi.getStatsByJour(
        dashMois, dashAnnee,
        dashConcession || undefined,
        dashCampagne   || undefined,
        dashService    || undefined,
      );
      setDashStats(r.data.stats || []);
    } catch (e) {
      console.error('[LeadsView] Erreur dashboard stats:', e);
    } finally {
      setDashLoading(false);
    }
  }, [dashMois, dashAnnee, dashConcession, dashCampagne, dashService]);

  useEffect(() => {
    if (activeTab === 'dashboard') loadDashboard();
  }, [activeTab, loadDashboard]);

  // Charger la liste des campagnes pour le filtre du tableau de bord.
  // Re-chargé quand la concession sélectionnée change (sinon on garde la
  // liste complète si aucune concession n'est filtrée).
  useEffect(() => {
    if (activeTab !== 'dashboard') return;
    campagnesMediasApi.getAll({
      actif: 1,
      ...(dashConcession ? { concession_id: dashConcession } : {}),
    })
      .then(r => setDashCampagnesList(r.data?.campagnes || []))
      .catch(() => setDashCampagnesList([]));
  }, [activeTab, dashConcession]);

  // Reset du filtre campagne quand on change de concession (cohérence).
  useEffect(() => {
    setDashCampagne('');
  }, [dashConcession]);

  const loadData = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filterStatut)     filters.statut        = filterStatut;
      if (filterVendeurId)  filters.vendeur_id    = filterVendeurId;
      if (filterConcession) filters.concession_id = filterConcession;
      const response = await leadsWebApi.getAll(filters);
      // Exclure les leads archivés de la vue par défaut
      setLeads(response.data.leads.filter(l => l.statut !== 'archive'));
    } catch (error) {
      console.error('Erreur chargement leads:', error);
    } finally {
      setLoading(false);
    }
  };
  loadDataRef.current = loadData;

  const loadArchives = async () => {
    try {
      const response = await leadsWebApi.getAll({ statut: 'archive' });
      setArchivedLeads(response.data.leads);
    } catch (error) {
      console.error('Erreur chargement archives leads:', error);
    }
  };

  // Export Excel de l'onglet Archives — inclut le N° de lead CRM360 saisi par l'opérateur
  const handleExportArchives = () => {
    if (!archivedLeads.length) return;
    const fmtDT = (s) => {
      if (!s) return '';
      const d = new Date(s);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };
    const rows = archivedLeads.map(l => ({
      'N° lead web':                l.id ?? '',
      'N° lead opérateur (CRM360)': l.crm360_lead_id || '',
      'Date réception':             fmtDT(l.date_reception),
      'Date archivage':             fmtDT(l.updated_at),
      'Statut':                     l.statut || '',
      'Priorité':                   l.priorite || '',
      'Service':                    l.service_concession || '',
      'Source':                     l.source || '',
      'Concession':                 l.concession_nom || '',
      'Vendeur':                    [l.vendeur_prenom, l.vendeur_nom].filter(Boolean).join(' '),
      'Civilité':                   l.gender || '',
      'Nom':                        l.name || '',
      'Prénom':                     l.firstname || '',
      'Email':                      l.email || '',
      'Téléphone':                  l.phone || '',
      'Sujet':                      l.subject || '',
      'Message':                    l.message || '',
      'Type véhicule':              l.type_vehicule || '',
      'Marque':                     l.brand || '',
      'Modèle':                     l.model || '',
      'Immatriculation':            l.immatriculation || '',
      'VIN / N° série':             l.serial_number || '',
      'Budget min':                 l.budget_min ?? '',
      'Budget max':                 l.budget_max ?? '',
      'Financement':                l.financement || '',
      'Date 1er contact':           fmtDT(l.date_premier_contact),
      'Date RDV':                   fmtDT(l.date_rdv),
      'Nb relances':                l.nb_relances ?? '',
      'Nb commentaires':            l.nb_commentaires ?? '',
      'URL origine':                l.url_origine || '',
      'IP':                         l.ip_address || '',
      'User-Agent':                 l.user_agent || '',
      'Date création':              fmtDT(l.created_at),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Archives Leads Web');
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `leads_web_archives_${date}.xlsx`);
  };

  // Bouton Export dans le header — visible uniquement sur l'onglet Archives
  useEffect(() => {
    if (!setHeaderAction) return;
    if (activeTab === 'archives') {
      setHeaderAction({
        secondary: [{
          label: (<><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>{' '}Export</>),
          onClick: () => handleExportArchives(),
        }],
      });
    } else {
      setHeaderAction(null);
    }
  }, [setHeaderAction, activeTab, archivedLeads]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadVendeurs = async () => {
    try {
      const response = await usersApi.getAssignables();
      setVendeurs(response.data.users);
    } catch (error) {
      console.error('Erreur chargement vendeurs:', error);
    }
  };

  const loadConcessions = async () => {
    try {
      const response = await concessionsApi.getAll();
      setConcessionsList(response.data.concessions || []);
    } catch (error) {
      console.error('Erreur chargement concessions:', error);
    }
  };

  const handleChangeConcession = async (leadId, concessionId) => {
    try {
      await leadsWebApi.update(leadId, { concession_id: concessionId ? parseInt(concessionId) : null });
      const c = concessionsList.find(c => String(c.id) === String(concessionId));
      const newConcessionId = concessionId ? parseInt(concessionId) : null;
      // Mettre à jour la modale immédiatement
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({
          ...prev,
          concession_id: newConcessionId,
          concession_nom: c?.nom || null,
        }));
      }
      // Si un filtre concession est actif et que le lead ne correspond plus, le retirer de la liste
      // Sinon mettre à jour localement
      if (filterConcession && String(newConcessionId) !== String(filterConcession)) {
        setLeads(prev => prev.filter(l => l.id !== leadId));
      } else {
        setLeads(prev => prev.map(l => l.id === leadId ? {
          ...l,
          concession_id: newConcessionId,
          concession_nom: c?.nom || null,
        } : l));
      }
    } catch (error) {
      console.error('Erreur changement concession:', error);
    }
  };

  const handleOpenLead = async (lead) => {
    setSelectedLead(lead);
    setShowModal(true);
    loadCommentaires(lead.id);
  };

  const loadCommentaires = async (leadId) => {
    try {
      const response = await leadsWebApi.getCommentaires(leadId);
      setCommentaires(response.data.commentaires);
    } catch (error) {
      console.error('Erreur chargement commentaires:', error);
    }
  };

  const handleAddCommentaire = async () => {
    if (!newCommentaire.trim()) return;
    try {
      await leadsWebApi.addCommentaire(selectedLead.id, { commentaire: newCommentaire, type: 'note' });
      setNewCommentaire('');
      loadCommentaires(selectedLead.id);
    } catch (error) {
      console.error('Erreur ajout commentaire:', error);
    }
  };

  const handleChangeStatut = async (leadId, newStatut) => {
    const chosen = allStatuts.find(s => s.code === newStatut);

    // CRM360 : flow spécial — prompt du numéro externe puis archivage
    if (newStatut === 'crm360') {
      setConfirmCrm360LeadId(leadId);
      setCrm360NumLead('');
      return;
    }

    // Autre statut marqué archive (ex: 'traite') : archivage direct, sans modal
    if (chosen?.archive) {
      try {
        await leadsWebApi.update(leadId, { statut: 'archive' });
        await leadsWebApi.addCommentaire(leadId, {
          commentaire: `Lead marqué "${chosen.nom}" — archivé`,
          type: 'archive'
        });
        setLeads(prev => prev.filter(l => l.id !== leadId));
        if (selectedLead?.id === leadId) setShowModal(false);
      } catch (error) {
        console.error('Erreur archivage:', error);
      }
      return;
    }

    // Changement de statut standard
    try {
      await leadsWebApi.update(leadId, { statut: newStatut });
      await leadsWebApi.addCommentaire(leadId, {
        commentaire: `Changement de statut → ${chosen?.nom || newStatut}`,
        type: 'changement_statut'
      });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, statut: newStatut } : l));
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({ ...prev, statut: newStatut }));
        loadCommentaires(leadId);
      }
    } catch (error) {
      console.error('Erreur changement statut:', error);
    }
  };

  const handleConfirmCrm360 = async (leadId) => {
    if (!crm360NumLead.trim()) return;
    try {
      await leadsWebApi.update(leadId, { statut: 'archive', crm360_lead_id: crm360NumLead.trim() });
      await leadsWebApi.addCommentaire(leadId, {
        commentaire: `Lead transféré vers CRM360 — N° ${crm360NumLead.trim()} — archivé`,
        type: 'archive'
      });
      setLeads(prev => prev.filter(l => l.id !== leadId));
      setConfirmCrm360LeadId(null);
      setCrm360NumLead('');
      if (selectedLead?.id === leadId) setShowModal(false);
    } catch (error) {
      console.error('Erreur CRM360:', error);
    }
  };

  const handleRestoreLead = async (leadId) => {
    try {
      await leadsWebApi.update(leadId, { statut: 'a_contacter' });
      await leadsWebApi.addCommentaire(leadId, {
        commentaire: 'Remis en liste active',
        type: 'changement_statut'
      });
      setArchivedLeads(prev => prev.filter(l => l.id !== leadId));
    } catch (error) {
      console.error('Erreur restauration lead:', error);
    }
  };

  // Vrai si le lead appartient à la concession SOGUAVASOREC (match sur le nom)
  const isSoguavasorecLead = (lead) => {
    const nom = (lead?.concession_nom || '').toUpperCase().replace(/[^A-Z]/g, '');
    return nom.includes('SOGUAVASOREC');
  };

  const handleAssignVendeur = async (leadId, vendeurId) => {
    try {
      const resp = await leadsWebApi.update(leadId, { vendeur_id: vendeurId || null });
      // Le backend renvoie le lead complet (incluant assigned_at posé au 1ʳᵉ
      // affectation). On fusionne pour que la sous-ligne suivi affiche tout
      // de suite « affecté en … » au lieu de rester sur « non affecté ».
      const updated = resp?.data?.lead || {};
      const v = vendeurs.find(v => String(v.id) === String(vendeurId));
      setLeads(prev => prev.map(l => l.id === leadId ? {
        ...l,
        ...updated,
        vendeur_id: vendeurId || null,
        vendeur_nom: v?.nom || updated.vendeur_nom || null,
        vendeur_prenom: v?.prenom || updated.vendeur_prenom || null,
      } : l));
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({ ...prev, ...updated, vendeur_id: vendeurId || null }));
      }
      // Proposition d'envoi d'e-mail au collaborateur pour les leads SOGUAVASOREC
      if (vendeurId && v?.email) {
        const baseLead = leads.find(l => l.id === leadId) || selectedLead;
        if (baseLead && isSoguavasorecLead(baseLead)) {
          setEmailPrompt({ lead: { ...baseLead, vendeur_id: vendeurId }, vendeur: v });
        }
      }
    } catch (error) {
      console.error('Erreur attribution vendeur:', error);
    }
  };

  // Construit un mailto: avec tous les détails structurés du lead (ouvre Outlook)
  const buildLeadMailto = (lead, vendeur) => {
    const fmtDate = (d) => {
      if (!d) return '—';
      try { return new Date(d).toLocaleString('fr-FR'); } catch { return String(d); }
    };
    const fmtBudget = (min, max) => {
      if (!min && !max) return '—';
      if (min && max) return `${Number(min).toLocaleString('fr-FR')} € – ${Number(max).toLocaleString('fr-FR')} €`;
      if (min) return `${Number(min).toLocaleString('fr-FR')} € +`;
      return `Jusqu'à ${Number(max).toLocaleString('fr-FR')} €`;
    };
    const lines = [
      `Bonjour ${(vendeur.prenom || '')} ${(vendeur.nom || '')},`.replace(/\s+,$/, ',').replace(/\s+/g, ' ').trim(),
      '',
      'Un nouveau lead vient de vous être affecté. Merci de le prendre en charge dans les meilleurs délais.',
      '',
      '— STATUT —',
      'Nouveau lead',
      '',
      '— CLIENT —',
      `Nom / Prénom : ${(lead.firstname || '')} ${(lead.name || '')}`.trim() || '—',
      `Téléphone    : ${lead.phone || '—'}`,
      `Email        : ${lead.email || '—'}`,
      '',
      '— VÉHICULE —',
      `Type         : ${lead.type_vehicule || '—'}`,
      `Marque       : ${lead.brand || '—'}`,
      `Modèle       : ${lead.model || '—'}`,
      ...(lead.immatriculation ? [`Immat        : ${lead.immatriculation}`] : []),
      ...(lead.serial_number ? [`N° série     : ${lead.serial_number}`] : []),
      `Budget       : ${fmtBudget(lead.budget_min, lead.budget_max)}`,
      ...(lead.financement ? [`Financement  : ${lead.financement}`] : []),
      '',
      '— SUIVI —',
      `Service      : ${lead.service_concession || 'VN'}`,
      `Source       : ${lead.source || '—'}`,
      `Priorité     : ${lead.priorite || 'normale'}`,
      `Concession   : ${lead.concession_nom || '—'}`,
      `Reçu le      : ${fmtDate(lead.date_reception)}`,
      ...(lead.subject ? [`Sujet        : ${lead.subject}`] : []),
      ...(lead.commande ? [`N° commande  : ${lead.commande}`] : []),
      '',
      '— MESSAGE DU CLIENT —',
      lead.message || '(aucun message)',
      '',
      '—',
      'CRM Atelier Pro',
    ];
    const subject = `Nouveau lead — ${(lead.firstname || '').trim()} ${(lead.name || '').trim()}`.trim();
    const body = lines.join('\r\n');
    return `mailto:${encodeURIComponent(vendeur.email)}`
      + `?subject=${encodeURIComponent(subject)}`
      + `&body=${encodeURIComponent(body)}`;
  };

  const handleConfirmEmailPrompt = () => {
    if (!emailPrompt) return;
    const url = buildLeadMailto(emailPrompt.lead, emailPrompt.vendeur);
    // Ouvre le client de messagerie par défaut (Outlook si configuré)
    window.location.href = url;
    setEmailPrompt(null);
  };

  const handleChangeService = async (leadId, serviceCode) => {
    try {
      await leadsWebApi.update(leadId, { service_concession: serviceCode });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, service_concession: serviceCode } : l));
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({ ...prev, service_concession: serviceCode }));
      }
    } catch (error) {
      console.error('Erreur changement service:', error);
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Supprimer définitivement ce lead ?')) return;
    try {
      await leadsWebApi.delete(leadId);
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Erreur suppression lead:', error);
    }
  };

  // ── Transformation lead web → demande client ──
  const openTransformModal = (lead) => {
    // Q3 option a : sujet vide, l'opérateur choisit toujours
    // Pré-sélection du responsable si on connaît un responsable lié au vendeur du lead
    const respId = lead.vendeur_id
      ? transformResponsables.find(r => r.user_id === lead.vendeur_id)?.id
      : null;
    // Pré-remplissage des champs transmis à la demande client (l'opérateur peut tout modifier)
    setTransformModal({
      lead,
      sujet: '',
      responsable_id: respId ? String(respId) : '',
      fields: {
        nom: lead.name || '',
        prenom: lead.firstname || '',
        telephone: lead.phone || '',
        email: lead.email || '',
        marque: lead.brand || '',
        modele: lead.model || '',
        immatriculation: lead.immatriculation || '',
        vin: lead.serial_number || '',
        bon_de_commande: lead.commande || '',
        commentaire_initial: lead.message || ''
      }
    });
  };

  const updateTransformField = (key, value) => {
    setTransformModal(m => m ? { ...m, fields: { ...m.fields, [key]: value } } : m);
  };

  const handleConfirmTransform = async () => {
    if (!transformModal) return;
    const { lead, sujet, responsable_id, fields } = transformModal;
    if (!sujet) { alert('Sélectionnez un sujet'); return; }
    if (!responsable_id) { alert('Sélectionnez un responsable'); return; }
    if (!fields.telephone || !fields.telephone.trim()) { alert('Le téléphone est requis'); return; }
    try {
      setTransformSaving(true);
      const r = await leadsWebApi.transformToDemande(lead.id, { sujet, responsable_id, overrides: fields });
      const demandeId = r.data?.demande?.id;
      // Synthèse locale du commentaire « transformation » créé côté backend pour rafraîchir
      // immédiatement la sous-ligne « dernière action » de la vignette sans recharger la liste.
      const transformPatch = {
        transformed_to_demande_id: demandeId,
        last_comment_text: `Transformé en demande client #${demandeId}`,
        last_comment_type: 'transformation',
        last_comment_at: new Date().toISOString(),
        last_comment_user_prenom: user?.prenom || null,
        last_comment_user_nom: user?.nom || null,
        nb_commentaires: (parseInt(lead.nb_commentaires) || 0) + 1
      };
      setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, ...transformPatch } : l));
      setSelectedLead(prev => prev && prev.id === lead.id ? { ...prev, ...transformPatch } : prev);
      setTransformModal(null);
      alert(`✅ Demande client #${demandeId} créée. Vous pouvez la retrouver dans Demandes Clients.`);
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 409 && data?.demande_id) {
        alert(`Ce lead a déjà été transformé en demande client #${data.demande_id}.`);
        setSelectedLead(prev => prev && prev.id === lead.id
          ? { ...prev, transformed_to_demande_id: data.demande_id }
          : prev);
        setTransformModal(null);
      } else {
        alert(data?.error || 'Erreur lors de la transformation');
      }
    } finally {
      setTransformSaving(false);
    }
  };

  const goToDemande = (demandeId) => {
    if (!demandeId) return;
    // Stocke l'id à pré-ouvrir et bascule sur la page Demandes via event sidebar
    try { localStorage.setItem('demandes_open_id', String(demandeId)); } catch (e) {}
    window.dispatchEvent(new CustomEvent('crm-navigate', { detail: { page: 'demandes' } }));
  };

  // ── Sélection en masse ──
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedLeads.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedLeads.map(l => l.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Supprimer définitivement ${selectedIds.size} lead(s) ?`)) return;
    try {
      await leadsWebApi.bulkDelete([...selectedIds]);
      setSelectedIds(new Set());
      loadData();
    } catch (error) {
      console.error('Erreur suppression en masse:', error);
      alert('Erreur lors de la suppression en masse');
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedIds.size === 0 || !bulkAction || bulkValue === '') return;
    try {
      let value = bulkValue;
      // Si le statut choisi archive le lead, on stocke 'archive' côté DB
      // (cohérence avec handleChangeStatut single-lead + crm360)
      if (bulkAction === 'statut') {
        const chosen = allStatuts.find(s => s.code === bulkValue);
        if (chosen?.archive) value = 'archive';
      }
      const data = { [bulkAction]: value || null };
      await leadsWebApi.bulkUpdate([...selectedIds], data);
      setSelectedIds(new Set());
      setBulkAction('');
      setBulkValue('');
      loadData();
    } catch (error) {
      console.error('Erreur mise à jour en masse:', error);
      alert('Erreur lors de la mise à jour en masse');
    }
  };

  const getPrioriteIcon = (priorite) => {
    if (priorite === 'haute') return '🔴';
    if (priorite === 'basse') return '🟢';
    return '🟡';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Affichage de la date de réception adapté au contexte :
  //  - aujourd'hui : « aujourd'hui à 14:32 » (heure précise utile au quotidien)
  //  - hier        : « hier à 14:32 »
  //  - plus ancien : « 28/04/2026 »
  const formatReception = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    const now = new Date();
    const isSameDay = d.toDateString() === now.toDateString();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday = d.toDateString() === yesterday.toDateString();
    const hm = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    if (isSameDay) return `aujourd'hui à ${hm}`;
    if (isYesterday) return `hier à ${hm}`;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  // Tooltip plein : « 28/04/2026 à 14:32 »
  const formatReceptionFull = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return `${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })} à ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
  };
  // Date « calendaire » sans conversion fuseau, pour les valeurs envoyées
  // sans heure (Make stocke `JJ/MM/AAAA` à minuit UTC). En Antilles (UTC-4),
  // une conversion TZ classique ferait reculer d'un jour. Si la valeur est
  // pile à minuit UTC, on extrait directement la date ISO ; sinon on convertit
  // normalement (cas où la source a fourni une heure).
  const formatDateCalendar = (dateString) => {
    if (!dateString) return '-';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '-';
    if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0) {
      return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
    }
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  // True si date_reception et date_lead_source représentent le même jour
  // calendaire affiché (date_reception → fuseau navigateur, date_lead_source
  // → UTC si à minuit pile, sinon fuseau navigateur). Permet de masquer la
  // ligne « source » quand elle apporte zéro information additionnelle.
  const isSameDayAsSource = (recIso, srcIso) => {
    if (!recIso || !srcIso) return false;
    const rec = new Date(recIso);
    const src = new Date(srcIso);
    if (isNaN(rec.getTime()) || isNaN(src.getTime())) return false;
    const recKey = `${rec.getFullYear()}-${rec.getMonth()}-${rec.getDate()}`;
    let srcKey;
    if (src.getUTCHours() === 0 && src.getUTCMinutes() === 0 && src.getUTCSeconds() === 0) {
      srcKey = `${src.getUTCFullYear()}-${src.getUTCMonth()}-${src.getUTCDate()}`;
    } else {
      srcKey = `${src.getFullYear()}-${src.getMonth()}-${src.getDate()}`;
    }
    return recKey === srcKey;
  };

  // Durée relative compacte « il y a 2j », « 4h », « 12 min », « 35 s »
  const formatDelay = (fromIso, toIso) => {
    if (!fromIso || !toIso) return null;
    const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
    if (!isFinite(ms) || ms < 0) return null;
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec} s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) {
      const remMin = min % 60;
      return remMin === 0 ? `${h} h` : `${h}h${String(remMin).padStart(2, '0')}`;
    }
    const j = Math.floor(h / 24);
    if (j < 14) {
      const remH = h % 24;
      return remH === 0 ? `${j} j` : `${j}j ${remH}h`;
    }
    return `${j} j`;
  };
  // Date « il y a … » par rapport à maintenant
  const formatAgo = (iso) => {
    if (!iso) return null;
    const d = formatDelay(iso, new Date().toISOString());
    return d ? `il y a ${d}` : null;
  };

  // Icône pour la dernière action du lead (commentaire le plus récent).
  const lastCommentIcon = (type) => {
    switch (type) {
      case 'archive':           return '📦';
      case 'changement_statut': return '🔄';
      case 'transformation':    return '🔁';
      case 'note':              return '📝';
      default:                  return '💬';
    }
  };

  // Rendu d'une sous-ligne « dernière action » pour une vignette de lead.
  // null si le lead n'a aucun commentaire.
  const renderLastComment = (lead) => {
    if (!lead?.last_comment_text) return null;
    const author = [lead.last_comment_user_prenom, lead.last_comment_user_nom].filter(Boolean).join(' ').trim();
    const tooltip = [
      lead.last_comment_at ? `Le ${formatReceptionFull(lead.last_comment_at)}` : null,
      author ? `par ${author}` : null,
      lead.last_comment_text
    ].filter(Boolean).join(' — ');
    return (
      <div
        className="lead-card-last-comment"
        title={tooltip}
        onClick={(e) => e.stopPropagation()}
      >
        <span className="lead-last-comment-ico">{lastCommentIcon(lead.last_comment_type)}</span>
        <span className="lead-last-comment-text">{lead.last_comment_text}</span>
      </div>
    );
  };

  const formatBudget = (min, max) => {
    if (!min && !max) return '-';
    if (!max) return `${min.toLocaleString()}€+`;
    if (!min) return `Jusqu'à ${max.toLocaleString()}€`;
    return `${min.toLocaleString()}€ – ${max.toLocaleString()}€`;
  };

  // Mapper une source brute vers son label
  const getSourceLabel = (source) => {
    if (!source) return 'inconnue';
    const match = sourcesMapping.find(sm => source.toLowerCase().includes(sm.pattern.toLowerCase()));
    return match ? match.label : source;
  };

  const uniqueSourceLabels = [...new Set(leads.map(l => getSourceLabel(l.source)))].sort();

  const filteredLeads = leads.filter(l => {
    // Filtre par onglet service actif
    if (serviceCodes.includes(activeTab) && (l.service_concession || 'VN') !== activeTab) return false;
    if (filterSource && getSourceLabel(l.source) !== filterSource) return false;
    // Cohérence avec filteredArchives + garde-fou si un lead d'une autre concession
    // se retrouvait en state (race SSE pendant un changement de filtre, etc.).
    if (filterConcession && String(l.concession_id || '') !== String(filterConcession)) return false;
    if (filterNonAssigne && l.vendeur_id) return false;
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      l.firstname?.toLowerCase().includes(s) ||
      l.name?.toLowerCase().includes(s) ||
      l.email?.toLowerCase().includes(s) ||
      l.phone?.includes(s) ||
      l.brand?.toLowerCase().includes(s) ||
      l.model?.toLowerCase().includes(s)
    );
  });

  const filteredArchives = archivedLeads.filter(l => {
    // filterStatut volontairement ignoré sur les archives (toutes = 'archive')
    if (filterSource && getSourceLabel(l.source) !== filterSource) return false;
    if (filterVendeurId && String(l.vendeur_id || '') !== String(filterVendeurId)) return false;
    if (filterConcession && String(l.concession_id || '') !== String(filterConcession)) return false;
    if (filterNonAssigne && l.vendeur_id) return false;
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      l.firstname?.toLowerCase().includes(s) ||
      l.name?.toLowerCase().includes(s) ||
      l.email?.toLowerCase().includes(s) ||
      l.phone?.includes(s) ||
      l.brand?.toLowerCase().includes(s) ||
      l.model?.toLowerCase().includes(s) ||
      l.crm360_lead_id?.toLowerCase().includes(s)
    );
  });

  const sortedLeads = [...filteredLeads].sort((a, b) => {
    let valA, valB;
    if (sortCol === 'date_reception') {
      valA = new Date(a.date_reception || 0).getTime();
      valB = new Date(b.date_reception || 0).getTime();
    } else if (sortCol === 'client') {
      valA = `${a.name || ''} ${a.firstname || ''}`.toLowerCase();
      valB = `${b.name || ''} ${b.firstname || ''}`.toLowerCase();
    } else {
      valA = (a[sortCol] || '').toString().toLowerCase();
      valB = (b[sortCol] || '').toString().toLowerCase();
    }
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const currentList = activeTab === 'archives' ? filteredArchives : sortedLeads;
  const currentIndex = selectedLead ? currentList.findIndex(l => l.id === selectedLead.id) : -1;

  const navigateLead = useCallback((direction) => {
    if (!selectedLead || currentList.length === 0) return;
    const idx = currentList.findIndex(l => l.id === selectedLead.id);
    const nextIdx = idx + direction;
    if (nextIdx < 0 || nextIdx >= currentList.length) return;
    const nextLead = currentList[nextIdx];
    setSelectedLead(nextLead);
    setCommentaires([]);
    loadCommentaires(nextLead.id);
  }, [selectedLead, currentList]); // eslint-disable-line

  useEffect(() => {
    if (!showModal) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft')  navigateLead(-1);
      if (e.key === 'ArrowRight') navigateLead(1);
      if (e.key === 'Escape')     setShowModal(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [showModal, navigateLead]);

  // ── Tableau de bord : pivot source × jour ───────────────────────────────
  const buildPivot = () => {
    const sources = [...new Set(dashStats.map(r => r.source_label || r.source))].sort();
    const jours   = [...new Set(dashStats.map(r => r.jour))].sort();
    const map = {};
    // Campagne par jour : NULL = aucune campagne sur ce jour ; '__multi__' =
    // plusieurs campagnes (badge « Multiple ») ; sinon nom de campagne.
    const campagneParJour = {};
    dashStats.forEach(r => {
      const src = r.source_label || r.source;
      if (!map[r.jour]) map[r.jour] = {};
      map[r.jour][src] = (map[r.jour][src] || 0) + r.total;
      if (campagneParJour[r.jour] === undefined) campagneParJour[r.jour] = r.campagne_nom ?? null;
    });
    const totauxSources = {};
    sources.forEach(s => {
      totauxSources[s] = dashStats.filter(r => (r.source_label || r.source) === s).reduce((a, b) => a + b.total, 0);
    });
    const totauxJours = {};
    jours.forEach(j => {
      totauxJours[j] = sources.reduce((a, s) => a + (map[j]?.[s] || 0), 0);
    });
    const grandTotal = dashStats.reduce((a, r) => a + r.total, 0);
    return { sources, jours, map, totauxSources, totauxJours, grandTotal, campagneParJour };
  };

  const MOIS_LABELS = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

  const renderDashboard = () => {
    const { sources, jours, map, totauxSources, totauxJours, grandTotal, campagneParJour } = buildPivot();
    const anneeMin = new Date().getFullYear() - 2;
    const annees = Array.from({ length: 5 }, (_, i) => anneeMin + i);

    return (
      <div className="lv-dash-wrap">
        {/* Filtres mois/année */}
        <div className="lv-dash-header">
          <div className="lv-dash-filters">
            <select
              value={dashMois}
              onChange={e => setDashMois(e.target.value === '' ? '' : parseInt(e.target.value))}
              className="lv-dash-select"
            >
              <option value="">Toutes les périodes</option>
              {MOIS_LABELS.map((l, i) => (
                <option key={i+1} value={i+1}>{l}</option>
              ))}
            </select>
            <select
              value={dashAnnee}
              onChange={e => setDashAnnee(parseInt(e.target.value))}
              className="lv-dash-select"
            >
              {annees.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select
              value={dashService}
              onChange={e => setDashService(e.target.value)}
              className={`lv-dash-select ${dashService ? 'active-filter' : ''}`}
            >
              <option value="">Tous les services</option>
              {SERVICES.map(s => (
                <option key={s.code} value={s.code}>{s.label.replace('Leads ', '')}</option>
              ))}
            </select>
            {(['super-admin', 'admin', 'teleoperateur'].includes(user?.role) || isPrestataire) && !user?.concession_id && concessionsList.length > 0 && (
              <select
                value={dashConcession}
                onChange={e => setDashConcession(e.target.value)}
                className="lv-dash-select"
              >
                <option value="">Toutes les concessions</option>
                {concessionsList.map(c => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </select>
            )}
            {dashCampagnesList.length > 0 && (
              <select
                value={dashCampagne}
                onChange={e => setDashCampagne(e.target.value)}
                className={`lv-dash-select ${dashCampagne ? 'active-filter' : ''}`}
              >
                <option value="">Toutes les campagnes</option>
                {dashCampagnesList.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.nom}{!c.actif ? ' (désactivée)' : ''}
                  </option>
                ))}
              </select>
            )}
            {dashLoading && <span className="lv-dash-loading">Chargement…</span>}
          </div>
          {!dashLoading && grandTotal > 0 && (
            <div className="lv-dash-total-badge">{grandTotal} lead{grandTotal > 1 ? 's' : ''} au total</div>
          )}
        </div>

        {/* Tableau croisé */}
        {!dashLoading && jours.length === 0 && (
          <div className="lv-dash-empty">Aucun lead reçu sur {dashMois ? 'cette période' : String(dashAnnee)}.</div>
        )}
        {!dashLoading && jours.length > 0 && (
          <div className="lv-dash-table-wrap">
            <table className="lv-dash-table">
              <thead>
                <tr>
                  <th className="lv-dash-th-jour">{dashMois ? 'Jour' : 'Mois'}</th>
                  <th>Campagne</th>
                  {sources.map(s => <th key={s}>{s}</th>)}
                  <th className="lv-dash-th-total">Total</th>
                </tr>
              </thead>
              <tbody>
                {jours.map(j => {
                  const d = new Date(j);
                  const label = dashMois
                    ? `${String(d.getUTCDate()).padStart(2,'0')}/${String(d.getUTCMonth()+1).padStart(2,'0')}`
                    : MOIS_LABELS[d.getUTCMonth()];
                  const jourTotal = totauxJours[j] || 0;
                  const campagne = campagneParJour[j];
                  return (
                    <tr key={j}>
                      <td className="lv-dash-td-jour">{label}</td>
                      <td className="lv-dash-td-campagne">
                        {campagne === '__multi__'
                          ? <span className="lv-dash-campagne-multi" title="Plusieurs campagnes ce jour-là">Multiple</span>
                          : campagne
                            ? <span className="lv-dash-campagne-tag">{campagne}</span>
                            : <span className="lv-dash-cell-empty">—</span>}
                      </td>
                      {sources.map(s => {
                        const v = map[j]?.[s] || 0;
                        return (
                          <td key={s} className={v > 0 ? 'lv-dash-cell-active' : 'lv-dash-cell-empty'}>
                            {v > 0 ? v : '—'}
                          </td>
                        );
                      })}
                      <td className="lv-dash-td-total">{jourTotal > 0 ? jourTotal : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="lv-dash-total-row">
                  <td className="lv-dash-td-jour">Total</td>
                  <td></td>
                  {sources.map(s => <td key={s}>{totauxSources[s] || 0}</td>)}
                  <td className="lv-dash-td-total">{grandTotal}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    );
  };

  const handleReset = () => {
    setFilterStatut('');
    setFilterVendeurId('');
    setFilterConcession('');
    setFilterSource('');
    setFilterNonAssigne(false);
    setSearchTerm('');
    setSortCol('date_reception');
    setSortDir('desc');
  };

  // activeFilterStyle supprimé — géré par la classe .page-select.active-filter

  const vendeurOptions = [
    { value: '', label: 'Non assigné' },
    ...vendeurs.map(v => ({ value: String(v.id), label: `${v.prenom || ''} ${v.nom}`.trim() }))
  ];

  return (
    <div className="page-container leads-view">
      {/* Badge éphémère : modification distante reçue via SSE */}
      {recentRemoteEdit && (
        <div className="leads-remote-edit-badge" role="status" aria-live="polite">
          🔄 Modifié par <strong>{recentRemoteEdit.who}</strong>
        </div>
      )}
      {/* ── Barre d'onglets ── */}
      <div className="page-tabs">
        {!isPrestataire && SERVICES.map(s => (
          <button key={s.code} className={activeTab === s.code ? 'active' : ''} onClick={() => setActiveTab(s.code)}>
            {s.label} <span className="tab-count">{leads.filter(l => (l.service_concession || 'VN') === s.code).length}</span>
          </button>
        ))}
        {!isPrestataire && (
          <button className={activeTab === 'archives' ? 'active' : ''} onClick={() => setActiveTab('archives')}>
            🗄️ Archives
          </button>
        )}
        <button className={activeTab === 'dashboard' ? 'active' : ''} onClick={() => setActiveTab('dashboard')}>
          📊 Tableau de bord
        </button>
        {canViewSuivi && (
          <button className={activeTab === 'funnel' ? 'active' : ''} onClick={() => setActiveTab('funnel')}>
            🪜 Funnel
          </button>
        )}
        {canViewSuivi && (
          <button className={activeTab === 'suivi' ? 'active' : ''} onClick={() => setActiveTab('suivi')}>
            👥 Suivi collaborateurs
          </button>
        )}
        {canViewSuivi && (
          <button className={activeTab === 'config' ? 'active' : ''} onClick={() => setActiveTab('config')}>
            ⚙️ Configuration
          </button>
        )}
        {isAdmin && (
          <button className={activeTab === 'crm360' ? 'active' : ''} onClick={() => setActiveTab('crm360')}>
            📥 Import CRM360
          </button>
        )}
      </div>

      {/* ── Tableau de bord ── */}
      {activeTab === 'dashboard' && renderDashboard()}

      {activeTab === 'suivi' && canViewSuivi && (
        <SuiviLeadsCollaborateurs concessionId={user?.concession_id || null} />
      )}

      {activeTab === 'config' && canViewSuivi && (
        <ConfigVendeursLeadsWeb user={user} concessionsList={concessionsList} />
      )}

      {activeTab === 'funnel' && canViewSuivi && (
        <LeadsFunnel user={user} concessionsList={concessionsList} />
      )}

      {activeTab === 'crm360' && isAdmin && (
        <LeadsCrm360Import />
      )}

      {/* ── Vue Leads + Archives (filtrée + toolbar commune) ── */}
      {(serviceCodes.includes(activeTab) || activeTab === 'archives') && <>

      {/* Rappel vendeurs absents (managers/super-admin uniquement, sur VN et VO) */}
      {(activeTab === 'VN' || activeTab === 'VO') && renderAbsentsBlock(activeTab)}

      {/* FILTRES */}
      <div className="page-toolbar">
        <input
          type="text"
          className="page-search"
          placeholder="🔍 Rechercher..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        {activeTab !== 'archives' && (
          <select className={`page-select${filterStatut ? ' active-filter' : ''}`} value={filterStatut} onChange={(e) => setFilterStatut(e.target.value)}>
            <option value="">Tous les statuts</option>
            {statutsForActiveTab.filter(s => !s.archive).map(s => <option key={s.code} value={s.code}>{s.nom}</option>)}
          </select>
        )}

        {uniqueSourceLabels.length > 0 && (
          <select className={`page-select${filterSource ? ' active-filter' : ''}`} value={filterSource} onChange={(e) => setFilterSource(e.target.value)}>
            <option value="">Toutes sources</option>
            {uniqueSourceLabels.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}

        <select className={`page-select${filterVendeurId ? ' active-filter' : ''}`} value={filterVendeurId} onChange={(e) => setFilterVendeurId(e.target.value)}>
          <option value="">Tous les vendeurs</option>
          {vendeurs.map(v => <option key={v.id} value={v.id}>{v.prenom || ''} {v.nom}</option>)}
        </select>

        {['super-admin', 'admin', 'teleoperateur'].includes(user?.role) && !user?.concession_id && concessionsList.length > 0 && (
          <select className={`page-select${filterConcession ? ' active-filter' : ''}`} value={filterConcession} onChange={(e) => setFilterConcession(e.target.value)}>
            <option value="">Toutes concessions</option>
            {concessionsList.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
          </select>
        )}

        <button
          onClick={() => setFilterNonAssigne(v => !v)}
          className={`page-select${filterNonAssigne ? ' active-filter' : ''}`}
          style={filterNonAssigne ? { background: 'var(--color-error)', color: 'var(--text-inverse)', borderColor: 'var(--color-error)' } : {}}
        >
          👤 Non assigné
        </button>

        <button onClick={handleReset} className="btn-secondary" title="Réinitialiser" style={{ height: 36, padding: '0 12px', flexShrink: 0 }}>↺</button>
      </div>

      {/* BARRE D'ACTIONS EN MASSE */}
      {bulkMode && serviceCodes.includes(activeTab) && (
        <div className="leads-bulk-bar">
          <div className="leads-bulk-left">
            <label className="leads-bulk-check-all">
              <input
                type="checkbox"
                checked={sortedLeads.length > 0 && selectedIds.size === sortedLeads.length}
                onChange={toggleSelectAll}
              />
              <span>Tout sélectionner</span>
            </label>
            {selectedIds.size > 0 && (
              <span className="leads-bulk-count">{selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}</span>
            )}
          </div>
          {selectedIds.size > 0 && (
            <div className="leads-bulk-actions">
              {(isSuperAdmin || canModify) && (
                <>
                  <select value={bulkAction} onChange={e => { setBulkAction(e.target.value); setBulkValue(''); }} className="leads-bulk-select">
                    <option value="">— Action en masse —</option>
                    <option value="statut">Changer le statut</option>
                    <option value="vendeur_id">Assigner un vendeur</option>
                    <option value="service_concession">Changer le service</option>
                    {concessionsList.length > 0 && <option value="concession_id">Changer la concession</option>}
                  </select>
                  {bulkAction === 'statut' && (
                    <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} className="leads-bulk-select">
                      <option value="">— Statut —</option>
                      {statutsForActiveTab.filter(s => s.code !== 'crm360').map(s => <option key={s.code} value={s.code}>{s.nom}{s.archive ? ' (archive)' : ''}</option>)}
                    </select>
                  )}
                  {bulkAction === 'vendeur_id' && (
                    <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} className="leads-bulk-select">
                      <option value="">— Vendeur —</option>
                      {vendeurs.map(v => <option key={v.id} value={v.id}>{v.prenom || ''} {v.nom}</option>)}
                    </select>
                  )}
                  {bulkAction === 'service_concession' && (
                    <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} className="leads-bulk-select">
                      <option value="">— Service —</option>
                      {SERVICES.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                    </select>
                  )}
                  {bulkAction === 'concession_id' && (
                    <select value={bulkValue} onChange={e => setBulkValue(e.target.value)} className="leads-bulk-select">
                      <option value="">— Concession —</option>
                      {concessionsList.map(c => <option key={c.id} value={String(c.id)}>{c.nom}</option>)}
                    </select>
                  )}
                  {bulkAction && bulkValue && (
                    <button onClick={handleBulkUpdate} className="leads-bulk-btn leads-bulk-btn-apply">Appliquer</button>
                  )}
                </>
              )}
              {(isSuperAdmin || canDelete) && (
                <button onClick={handleBulkDelete} className="leads-bulk-btn leads-bulk-btn-delete">
                  Supprimer la sélection
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {loading && <div className="loading">Chargement...</div>}

      {/* VUE ARCHIVES */}
      {!loading && activeTab === 'archives' && (
        <div className="leads-cards">
          {filteredArchives.length === 0 && (
            <div className="leads-empty">Aucun lead archivé{archivedLeads.length > 0 ? ' ne correspond aux filtres' : ''}</div>
          )}
          {filteredArchives.map(lead => (
            <div key={lead.id} className="lead-card lead-card-archived">
              <div className="lead-card-body">
                <div className="lead-card-left" onClick={() => handleOpenLead(lead)}>
                  <div className="lead-card-name">
                    <span className="lead-priorite">{getPrioriteIcon(lead.priorite)}</span>
                    <span className="lead-fullname">{lead.firstname} {lead.name}</span>
                    <span className="lead-date-stack">
                      <span className="lead-date" title={formatReceptionFull(lead.date_reception)}>{formatReception(lead.date_reception)}</span>
                      {lead.date_lead_source && !isSameDayAsSource(lead.date_reception, lead.date_lead_source) && (
                        <span className="lead-date-source" title={`Lead émis par la source le ${formatDateCalendar(lead.date_lead_source)}`}>
                          🌐 source {formatDateCalendar(lead.date_lead_source)}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="lead-card-coords">
                    {lead.phone && <span className="lead-coord"><span className="lead-coord-ico">📞</span>{lead.phone}</span>}
                    {lead.email && <span className="lead-coord"><span className="lead-coord-ico">✉️</span>{lead.email}</span>}
                    {lead.brand && <span className="lead-coord"><span className="lead-coord-ico">🚗</span>{lead.brand}{lead.model ? ` ${lead.model}` : ''}{lead.type_vehicule ? ` • ${lead.type_vehicule}` : ''}</span>}
                    {lead.vendeur_nom && <span className="lead-coord"><span className="lead-coord-ico">👤</span>{lead.vendeur_prenom} {lead.vendeur_nom}</span>}
                  </div>

                  {/* Sous-ligne suivi : reçu / affecté / traité (lecture seule en archives) */}
                  <div className="lead-card-suivi">
                    <span className="lead-suivi-item" title={`Reçu dans le CRM le ${formatReceptionFull(lead.date_reception)}`}>
                      <span className="lead-suivi-ico">📥</span>
                      reçu {formatAgo(lead.date_reception) || '—'}
                    </span>
                    <span className="lead-suivi-sep">•</span>
                    <span
                      className="lead-suivi-item"
                      title={lead.assigned_at ? `Affecté le ${formatReceptionFull(lead.assigned_at)}` : 'Pas d\'affectation tracée'}
                    >
                      <span className="lead-suivi-ico">✋</span>
                      {lead.assigned_at
                        ? <>affecté en {formatDelay(lead.date_reception, lead.assigned_at) || '—'}</>
                        : <>non affecté</>}
                    </span>
                    <span className="lead-suivi-sep">•</span>
                    <span
                      className="lead-suivi-item"
                      title={lead.traite_at ? `Traité le ${formatReceptionFull(lead.traite_at)}` : 'Date de traitement non tracée'}
                    >
                      <span className="lead-suivi-ico">✓</span>
                      {lead.traite_at && lead.assigned_at
                        ? <>traité en {formatDelay(lead.assigned_at, lead.traite_at) || '—'}</>
                        : lead.traite_at
                          ? <>traité {formatAgo(lead.traite_at) || ''}</>
                          : <>—</>}
                    </span>
                  </div>

                  {renderLastComment(lead)}
                </div>
                <div className="lead-card-actions">
                  {lead.phone && (
                    <a href={`tel:${lead.phone}`} className="lead-action-btn lead-action-tel" title={`Appeler ${lead.phone}`}>📞</a>
                  )}
                  {lead.email && (
                    <a href={`mailto:${lead.email}`} className="lead-action-btn lead-action-mail" title={`Email ${lead.email}`}>✉️</a>
                  )}
                  <button
                    className="lead-action-btn"
                    title="Remettre en liste active"
                    onClick={(e) => { e.stopPropagation(); handleRestoreLead(lead.id); }}
                    style={{ fontSize: '16px' }}
                  >
                    ↩️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* LISTE EN CARTES */}
      {!loading && serviceCodes.includes(activeTab) && (
        <div className="leads-cards">
          {sortedLeads.length === 0 && (
            <div className="leads-empty">Aucun lead</div>
          )}
          {sortedLeads.map(lead => (
            <div key={lead.id} className={`lead-card${bulkMode && selectedIds.has(lead.id) ? ' lead-card-selected' : ''}`}>
              {/* Ligne principale : infos + actions */}
              <div className="lead-card-body">
                {/* ── Checkbox mode masse ── */}
                {bulkMode && (
                  <div className="lead-card-checkbox" onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(lead.id)}
                      onChange={() => toggleSelect(lead.id)}
                    />
                  </div>
                )}
                {/* ── Colonne gauche ── */}
                <div className="lead-card-left" onClick={() => handleOpenLead(lead)}>
                  <div className="lead-card-name">
                    <span className="lead-priorite">{getPrioriteIcon(lead.priorite)}</span>
                    <span className="lead-fullname">{lead.firstname} {lead.name}</span>
                    <span className="lead-date-stack">
                      <span className="lead-date" title={formatReceptionFull(lead.date_reception)}>{formatReception(lead.date_reception)}</span>
                      {lead.date_lead_source && !isSameDayAsSource(lead.date_reception, lead.date_lead_source) && (
                        <span className="lead-date-source" title={`Lead émis par la source le ${formatDateCalendar(lead.date_lead_source)}`}>
                          🌐 source {formatDateCalendar(lead.date_lead_source)}
                        </span>
                      )}
                    </span>
                  </div>

                  <div className="lead-card-selects" onClick={e => e.stopPropagation()}>
                    <InlineStatusSelect
                      value={lead.statut}
                      options={buildStatutOptions(lead)}
                      onChange={(v) => handleChangeStatut(lead.id, v)}
                    />
                    <InlineStatusSelect
                      value={String(lead.vendeur_id || '')}
                      options={vendeurOptions}
                      onChange={(v) => handleAssignVendeur(lead.id, v)}
                      searchable
                    />
                  </div>

                  <div className="lead-card-coords">
                    {lead.phone && <span className="lead-coord"><span className="lead-coord-ico">📞</span>{lead.phone}</span>}
                    {lead.email && <span className="lead-coord"><span className="lead-coord-ico">✉️</span>{lead.email}</span>}
                    {lead.brand && <span className="lead-coord"><span className="lead-coord-ico">🚗</span>{lead.brand}{lead.model ? ` ${lead.model}` : ''}{lead.type_vehicule ? ` • ${lead.type_vehicule}` : ''}</span>}
                    {lead.source && <span className="lead-coord"><span className="lead-coord-ico">🌐</span>{getSourceLabel(lead.source)}</span>}
                    {user?.role === 'super-admin' && <span className="lead-coord"><span className="lead-coord-ico">🏢</span>{lead.concession_nom || '—'}</span>}
                  </div>

                  {/* Suivi de traitement : reçu / délai d'affectation / délai de traitement */}
                  <div className="lead-card-suivi">
                    <span className="lead-suivi-item" title={`Reçu dans le CRM le ${formatReceptionFull(lead.date_reception)}`}>
                      <span className="lead-suivi-ico">📥</span>
                      reçu {formatAgo(lead.date_reception) || '—'}
                    </span>
                    <span className="lead-suivi-sep">•</span>
                    <span
                      className="lead-suivi-item"
                      title={lead.assigned_at ? `Affecté le ${formatDate(lead.assigned_at)}` : 'Pas encore affecté à un vendeur'}
                    >
                      <span className="lead-suivi-ico">✋</span>
                      {lead.assigned_at
                        ? <>affecté en {formatDelay(lead.date_reception, lead.assigned_at) || '—'}</>
                        : <>non affecté</>}
                    </span>
                    <span className="lead-suivi-sep">•</span>
                    <span
                      className="lead-suivi-item"
                      title={lead.traite_at ? `Traité le ${formatDate(lead.traite_at)}` : 'Pas encore traité'}
                    >
                      <span className="lead-suivi-ico">✓</span>
                      {lead.traite_at && lead.assigned_at
                        ? <>traité en {formatDelay(lead.assigned_at, lead.traite_at) || '—'}</>
                        : lead.traite_at
                          ? <>traité {formatAgo(lead.traite_at) || ''}</>
                          : <>en cours</>}
                    </span>
                  </div>

                  {renderLastComment(lead)}
                </div>

                {/* ── Colonne droite : actions ── */}
                <div className="lead-card-actions">
                  {lead.phone && (
                    <a href={`tel:${lead.phone}`} className="lead-action-btn lead-action-tel" title={`Appeler ${lead.phone}`}>
                      📞
                    </a>
                  )}
                  {lead.email && (
                    <a
                      href={`mailto:${lead.email}?subject=Votre demande ${lead.brand ? `- ${lead.brand}` : ''}&body=Bonjour ${lead.firstname || ''},`}
                      className="lead-action-btn lead-action-mail"
                      title={`Envoyer un email à ${lead.email}`}
                    >
                      ✉️
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODALE ENVOI E-MAIL (leads SOGUAVASOREC) */}
      {emailPrompt && (
        <div className="modal-overlay">
          <div className="modal-content modal-confirm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>✉️ Prévenir le collaborateur par e-mail ?</h2>
              <button className="modal-close" onClick={() => setEmailPrompt(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '12px' }}>
                Ce lead <strong>SOGUAVASOREC</strong> vient d'être affecté à{' '}
                <strong>{emailPrompt.vendeur.prenom} {emailPrompt.vendeur.nom}</strong>.
              </p>
              <p style={{ fontSize: '13px', marginBottom: '8px' }}>
                Souhaitez-vous ouvrir Outlook pour envoyer un e-mail « Nouveau lead »
                à <strong>{emailPrompt.vendeur.email}</strong> avec tous les détails du lead ?
              </p>
              <div style={{
                background: 'var(--bg-muted)',
                border: '1px solid var(--border-default)',
                borderRadius: '6px',
                padding: '10px 12px',
                fontSize: '12px',
                color: 'var(--text-secondary)',
                marginTop: '12px'
              }}>
                <div><strong>Destinataire&nbsp;:</strong> {emailPrompt.vendeur.email}</div>
                <div><strong>Objet&nbsp;:</strong> Nouveau lead — {emailPrompt.lead.firstname} {emailPrompt.lead.name}</div>
                <div><strong>Contenu&nbsp;:</strong> tous les détails structurés du lead</div>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setEmailPrompt(null)} className="btn-secondary">Non, merci</button>
              <button onClick={handleConfirmEmailPrompt} className="btn-primary">
                ✉️ Ouvrir Outlook
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODALE CRM360 */}
      {confirmCrm360LeadId && (
        <div className="modal-overlay">
          <div className="modal-content modal-confirm" onClick={e => e.stopPropagation()}>
            <h3>Transférer vers CRM360</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '4px' }}>Ce lead sera archivé et marqué comme transféré vers CRM360.</p>
            <div style={{ marginTop: '12px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                N° de lead CRM360 <span style={{ color: 'light-dark(var(--color-error), var(--color-error-dark))' }}>*</span>
              </label>
              <input
                type="text"
                value={crm360NumLead}
                onChange={e => setCrm360NumLead(e.target.value)}
                placeholder="Ex : 45821"
                autoFocus
                style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border-default)', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                onKeyDown={e => { if (e.key === 'Enter' && crm360NumLead.trim()) handleConfirmCrm360(confirmCrm360LeadId); }}
              />
            </div>
            <div className="modal-actions" style={{ marginTop: '16px' }}>
              <button onClick={() => { setConfirmCrm360LeadId(null); setCrm360NumLead(''); }} className="btn-secondary">Annuler</button>
              <button onClick={() => handleConfirmCrm360(confirmCrm360LeadId)} className="btn-primary" disabled={!crm360NumLead.trim()}>Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DÉTAILS */}
      {showModal && selectedLead && (
        <div className="modal-overlay">
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-nav-arrows">
                <button
                  onClick={() => navigateLead(-1)}
                  disabled={currentIndex <= 0}
                  className="modal-nav-btn"
                  title="Lead précédent (←)"
                >‹</button>
                <span className="modal-nav-counter">{currentIndex + 1} / {currentList.length}</span>
                <button
                  onClick={() => navigateLead(1)}
                  disabled={currentIndex >= currentList.length - 1}
                  className="modal-nav-btn"
                  title="Lead suivant (→)"
                >›</button>
              </div>
              <h2>{getPrioriteIcon(selectedLead.priorite)} {selectedLead.firstname} {selectedLead.name}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
            </div>

            <div className="lead-details-compact">
              <div className="lead-summary-bar">
                <div className="lead-summary-item"><span className="lead-summary-label">📅 Date</span><span>{formatDate(selectedLead.date_reception)}</span></div>
                <div className="lead-summary-item"><span className="lead-summary-label">📞 Tél</span><a href={`tel:${selectedLead.phone}`} style={{ color: 'light-dark(var(--color-accent), var(--color-accent-dark))', fontWeight: '600' }}>{selectedLead.phone}</a></div>
                {selectedLead.email && <div className="lead-summary-item"><span className="lead-summary-label">✉️ Email</span><a href={`mailto:${selectedLead.email}`} style={{ color: 'light-dark(var(--color-accent), var(--color-accent-dark))' }}>{selectedLead.email}</a></div>}
                <div className="lead-summary-item"><span className="lead-summary-label">🏷️ Source</span><span>{selectedLead.source || '—'}</span></div>
              </div>

              <div className="lead-details-grid">
                <div className="lead-col">
                  <div className="lead-section-title">🚗 Véhicule</div>
                  <div className="lead-field"><span>Type</span><span>{selectedLead.type_vehicule || '—'}</span></div>
                  <div className="lead-field"><span>Marque / Modèle</span><span>{selectedLead.brand} {selectedLead.model}</span></div>
                  {selectedLead.immatriculation && <div className="lead-field"><span>Immat</span><span>{selectedLead.immatriculation}</span></div>}
                  {selectedLead.serial_number && <div className="lead-field"><span>N° série</span><span>{selectedLead.serial_number}</span></div>}
                  <div className="lead-field"><span>Budget</span><span>{formatBudget(selectedLead.budget_min, selectedLead.budget_max)}</span></div>
                  {selectedLead.financement && <div className="lead-field"><span>Financement</span><span>{selectedLead.financement}</span></div>}
                </div>
                <div className="lead-col">
                  <div className="lead-section-title">📊 Suivi</div>
                  <div className="lead-field">
                    <span>Statut</span>
                    <span onClick={e => e.stopPropagation()}>
                      <InlineStatusSelect
                        value={selectedLead.statut}
                        options={buildStatutOptions(selectedLead)}
                        onChange={(v) => handleChangeStatut(selectedLead.id, v)}
                      />
                    </span>
                  </div>
                  <div className="lead-field">
                    <span>Vendeur</span>
                    <span onClick={e => e.stopPropagation()}>
                      <InlineStatusSelect
                        value={String(selectedLead.vendeur_id || '')}
                        options={vendeurOptions}
                        onChange={(v) => handleAssignVendeur(selectedLead.id, v)}
                        searchable
                      />
                    </span>
                  </div>
                  <div className="lead-field">
                    <span>Service</span>
                    <span onClick={e => e.stopPropagation()}>
                      <InlineStatusSelect
                        value={selectedLead.service_concession || 'VN'}
                        options={SERVICES.map(s => ({ value: s.code, label: s.label.replace('Dem. ', '') }))}
                        onChange={(v) => handleChangeService(selectedLead.id, v)}
                      />
                    </span>
                  </div>
                  {user?.role === 'super-admin' && (
                    <div className="lead-field">
                      <span>🏢 Concession</span>
                      <span onClick={e => e.stopPropagation()}>
                        <InlineStatusSelect
                          value={String(selectedLead.concession_id || '')}
                          options={[
                            { value: '', label: '— Non assignée' },
                            ...concessionsList.map(c => ({ value: String(c.id), label: c.nom }))
                          ]}
                          onChange={(v) => handleChangeConcession(selectedLead.id, v)}
                        />
                      </span>
                    </div>
                  )}
                  {selectedLead.subject && <div className="lead-field"><span>Sujet</span><span>{selectedLead.subject}</span></div>}
                  {selectedLead.commande && <div className="lead-field"><span>N° commande</span><span>{selectedLead.commande}</span></div>}
                </div>
              </div>

              {selectedLead.message && (
                <div className="lead-message-block">
                  <div className="lead-section-title">💬 Message du client</div>
                  <p>{selectedLead.message}</p>
                </div>
              )}

              <div className="lead-section-title">🗒️ Historique des échanges</div>
              <div className="commentaires-list">
                {commentaires.length === 0 && <div style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '13px', padding: '8px 0' }}>Aucun commentaire</div>}
                {commentaires.map(c => (
                  <div key={c.id} className="commentaire">
                    <div className="commentaire-header">
                      <strong>{c.user_prenom} {c.user_nom}</strong>
                      <span>{formatDate(c.created_at)}</span>
                    </div>
                    <p>{c.commentaire}</p>
                  </div>
                ))}
              </div>

              <div className="add-commentaire">
                <textarea value={newCommentaire} onChange={(e) => setNewCommentaire(e.target.value)} placeholder="Ajouter un commentaire de suivi..." rows={2} />
                <button onClick={handleAddCommentaire} className="btn-primary" disabled={!newCommentaire.trim()}>Ajouter</button>
              </div>
            </div>

            <div className="modal-actions">
              {bulkMode && (
                <button onClick={() => handleDelete(selectedLead.id)} style={{ marginRight: 'auto', background: '#ef4444', color: 'var(--text-inverse)', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                  🗑️ Supprimer
                </button>
              )}
              {selectedLead.transformed_to_demande_id ? (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => goToDemande(selectedLead.transformed_to_demande_id)}
                  title={`Voir la demande client #${selectedLead.transformed_to_demande_id}`}
                  style={{ marginRight: 'auto' }}
                >
                  📁 Voir la demande #{selectedLead.transformed_to_demande_id}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => openTransformModal(selectedLead)}
                  style={{ marginRight: 'auto' }}
                >
                  🔁 Transformer en demande client
                </button>
              )}
              <button onClick={() => setShowModal(false)} className="btn-secondary">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Mini-modale "Transformer en demande client" */}
      {transformModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🔁 Transformer en demande client</h2>
              <button className="modal-close" onClick={() => !transformSaving && setTransformModal(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                Voici les champs qui seront transmis à la nouvelle demande client. Vous pouvez les corriger avant validation.
                Le lead reste en place et est marqué comme transformé pour éviter les doublons.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <label>
                  <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Sujet de la demande *</div>
                  <select
                    className="page-select"
                    style={{ width: '100%' }}
                    value={transformModal.sujet}
                    onChange={e => setTransformModal(m => ({ ...m, sujet: e.target.value }))}
                  >
                    <option value="">— Choisir un sujet —</option>
                    {transformSujets
                      .filter(s => s.actif !== false)
                      .map(s => <option key={s.id} value={s.nom}>{s.nom}</option>)}
                  </select>
                </label>

                <label>
                  <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>Responsable affecté *</div>
                  <select
                    className="page-select"
                    style={{ width: '100%' }}
                    value={transformModal.responsable_id}
                    onChange={e => setTransformModal(m => ({ ...m, responsable_id: e.target.value }))}
                  >
                    <option value="">— Choisir un responsable —</option>
                    {transformResponsables
                      .filter(r => !transformModal.lead.concession_id || parseInt(r.concession_id) === parseInt(transformModal.lead.concession_id))
                      .map(r => (
                        <option key={r.id} value={r.id}>
                          {r.nom} {r.prenom || ''}{r.service ? ` — ${r.service}` : ''}
                        </option>
                      ))}
                  </select>
                </label>

                <div style={{
                  marginTop: '4px',
                  padding: '12px',
                  border: '1px solid var(--border-default)',
                  borderRadius: '8px',
                  background: 'var(--bg-muted)'
                }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '10px', color: 'var(--text-secondary)' }}>
                    Champs transmis à la demande
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <label>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '3px' }}>Nom</div>
                      <input
                        type="text"
                        className="page-search"
                        style={{ width: '100%' }}
                        value={transformModal.fields.nom}
                        onChange={e => updateTransformField('nom', e.target.value)}
                      />
                    </label>
                    <label>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '3px' }}>Prénom</div>
                      <input
                        type="text"
                        className="page-search"
                        style={{ width: '100%' }}
                        value={transformModal.fields.prenom}
                        onChange={e => updateTransformField('prenom', e.target.value)}
                      />
                    </label>
                    <label>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '3px' }}>Téléphone *</div>
                      <input
                        type="tel"
                        className="page-search"
                        style={{ width: '100%' }}
                        value={transformModal.fields.telephone}
                        onChange={e => updateTransformField('telephone', e.target.value)}
                      />
                    </label>
                    <label>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '3px' }}>Email</div>
                      <input
                        type="email"
                        className="page-search"
                        style={{ width: '100%' }}
                        value={transformModal.fields.email}
                        onChange={e => updateTransformField('email', e.target.value)}
                      />
                    </label>
                    <label>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '3px' }}>Marque</div>
                      <input
                        type="text"
                        className="page-search"
                        style={{ width: '100%' }}
                        value={transformModal.fields.marque}
                        onChange={e => updateTransformField('marque', e.target.value)}
                      />
                    </label>
                    <label>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '3px' }}>Modèle</div>
                      <input
                        type="text"
                        className="page-search"
                        style={{ width: '100%' }}
                        value={transformModal.fields.modele}
                        onChange={e => updateTransformField('modele', e.target.value)}
                      />
                    </label>
                    <label>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '3px' }}>Immatriculation</div>
                      <input
                        type="text"
                        className="page-search"
                        style={{ width: '100%' }}
                        value={transformModal.fields.immatriculation}
                        onChange={e => updateTransformField('immatriculation', e.target.value.toUpperCase())}
                      />
                    </label>
                    <label>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '3px' }}>VIN</div>
                      <input
                        type="text"
                        className="page-search"
                        style={{ width: '100%' }}
                        value={transformModal.fields.vin}
                        onChange={e => updateTransformField('vin', e.target.value.toUpperCase())}
                      />
                    </label>
                    <label style={{ gridColumn: '1 / -1' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '3px' }}>Bon de commande</div>
                      <input
                        type="text"
                        className="page-search"
                        style={{ width: '100%' }}
                        value={transformModal.fields.bon_de_commande}
                        onChange={e => updateTransformField('bon_de_commande', e.target.value)}
                      />
                    </label>
                    <label style={{ gridColumn: '1 / -1' }}>
                      <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '3px' }}>Commentaire initial</div>
                      <textarea
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '8px 10px',
                          border: '1px solid var(--border-default)',
                          borderRadius: '6px',
                          background: 'var(--bg-input)',
                          color: 'var(--text-primary)',
                          fontFamily: 'inherit',
                          fontSize: '13px',
                          resize: 'vertical',
                          boxSizing: 'border-box'
                        }}
                        value={transformModal.fields.commentaire_initial}
                        onChange={e => updateTransformField('commentaire_initial', e.target.value)}
                      />
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '3px' }}>
                        Sera préfixé par <code>(Issu du lead web #{transformModal.lead.id})</code> dans la demande.
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setTransformModal(null)} disabled={transformSaving}>Annuler</button>
              <button className="btn-primary" onClick={handleConfirmTransform} disabled={transformSaving || !transformModal.sujet || !transformModal.responsable_id}>
                {transformSaving ? '…' : 'Créer la demande'}
              </button>
            </div>
          </div>
        </div>
      )}

      </> /* fin onglet leads */}
    </div>
  );
}

// ── Suivi collaborateurs Leads Web (super-admin) ──
const LEAD_SERVICES = { VN: { label: 'Leads VN', icon: '🚗' }, VO: { label: 'Leads VO', icon: '🚙' }, ATELIER: { label: 'Leads Atelier', icon: '🔧' }, PIECES: { label: 'Leads Pièces', icon: '⚙️' }, AUTRE: { label: 'Leads Autres', icon: '📋' } };

function SuiviLeadsCollaborateurs({ concessionId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('total_leads');
  const [sortDir, setSortDir] = useState('desc');
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const params = concessionId ? { concession_id: concessionId } : {};
        const res = await leadsWebApi.getSuiviCollaborateurs(params);
        setData(res.data.collaborateurs || []);
      } catch (err) {
        console.error('Erreur suivi collaborateurs leads-web:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [concessionId]);

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const sortRows = (rows) => [...rows].sort((a, b) => {
    let va, vb;
    if (sortField === 'collaborateur') { va = `${a.vendeur_nom} ${a.vendeur_prenom || ''}`; vb = `${b.vendeur_nom} ${b.vendeur_prenom || ''}`; }
    else if (sortField === 'total_leads') { va = Number(a.total_leads); vb = Number(b.total_leads); }
    else if (sortField === 'traites_aujourdhui') { va = Number(a.traites_aujourdhui); vb = Number(b.traites_aujourdhui); }
    else if (sortField === 'traites_hier') { va = Number(a.traites_hier); vb = Number(b.traites_hier); }
    else if (sortField === 'traites_7j') { va = Number(a.traites_7j); vb = Number(b.traites_7j); }
    else if (sortField === 'dernier_traitement') { va = a.dernier_traitement || ''; vb = b.dernier_traitement || ''; }
    else return 0;
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const sections = useMemo(() => {
    const order = ['VN', 'VO', 'ATELIER', 'PIECES', 'AUTRE', '_none'];
    const groups = {};
    data.forEach(c => {
      const key = c.service || '_none';
      const meta = LEAD_SERVICES[key] || { label: 'Non renseigné', icon: '❓' };
      if (!groups[key]) groups[key] = { key, label: meta.label, icon: meta.icon, entries: [], totalLeads: 0 };
      groups[key].entries.push(c);
      groups[key].totalLeads += Number(c.total_leads);
    });
    return order.filter(k => groups[k]).map(k => groups[k]);
  }, [data]);

  if (loading) return <div className="loading">Chargement...</div>;

  const thBase = { padding: '10px 12px', fontWeight: 600, fontSize: '13px', background: 'var(--bg-muted)', color: 'var(--text-primary)', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' };
  const sortIcon = (field) => sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ⇅';
  const tdStyle = { padding: '8px 12px', fontSize: '13px', borderBottom: '1px solid var(--border-default)' };

  const renderRow = (c) => (
    <tr key={c.vendeur_id}>
      <td style={tdStyle}>
        <div style={{ fontWeight: 600 }}>{c.vendeur_nom}</div>
        {c.vendeur_prenom && <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{c.vendeur_prenom}</div>}
      </td>
      <td style={{ ...tdStyle, textAlign: 'center' }}>
        <span style={{
          display: 'inline-block', padding: '3px 10px', borderRadius: 6, fontWeight: 700, fontSize: 13,
          background: Number(c.total_leads) > 5 ? 'light-dark(#fee2e2, var(--color-error-light))' : Number(c.total_leads) > 2 ? 'light-dark(#fef9c3, var(--color-warning-light))' : 'light-dark(#e0f2fe, var(--color-info-light))',
          color: Number(c.total_leads) > 5 ? 'light-dark(#b91c1c, var(--color-error-dark))' : Number(c.total_leads) > 2 ? 'light-dark(#854d0e, var(--color-warning-dark))' : 'light-dark(#0369a1, var(--color-info-dark))',
        }}>{c.total_leads}</span>
      </td>
      <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>
        {Number(c.traites_aujourdhui) > 0
          ? <span style={{ color: 'light-dark(var(--color-success), var(--color-success-dark))' }}>{c.traites_aujourdhui}</span>
          : <span style={{ color: 'var(--text-muted)' }}>0</span>}
      </td>
      <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>
        {Number(c.traites_hier) > 0
          ? <span style={{ color: 'light-dark(var(--color-success), var(--color-success-dark))' }}>{c.traites_hier}</span>
          : <span style={{ color: 'var(--text-muted)' }}>0</span>}
      </td>
      <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>
        {Number(c.traites_7j) > 0
          ? <span style={{ color: 'var(--color-info, #0369a1)' }}>{c.traites_7j}</span>
          : <span style={{ color: 'var(--text-muted)' }}>0</span>}
      </td>
      <td style={tdStyle}>
        {c.dernier_traitement
          ? new Date(c.dernier_traitement).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
          : <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Jamais</span>}
      </td>
    </tr>
  );

  const totalLeads = data.reduce((s, c) => s + Number(c.total_leads), 0);
  const totalAujourdhui = data.reduce((s, c) => s + Number(c.traites_aujourdhui || 0), 0);
  const totalHier = data.reduce((s, c) => s + Number(c.traites_hier), 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        {sections.map(s => (
          <div key={s.key} onClick={() => setCollapsed(prev => ({ ...prev, [s.key]: !prev[s.key] }))}
            style={{ flex: '1 1 140px', maxWidth: 200, padding: '12px 14px', borderRadius: 10, background: 'var(--bg-surface)', border: '1px solid var(--border-default)', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 16 }}>{s.icon}</span>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{s.label}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.entries.length} collab.</span>
              <span style={{ fontWeight: 700, fontSize: 15, color: s.totalLeads > 10 ? 'light-dark(#b91c1c, var(--color-error-dark))' : s.totalLeads > 0 ? 'light-dark(#854d0e, var(--color-warning-dark))' : 'var(--text-muted)' }}>{s.totalLeads} leads</span>
            </div>
          </div>
        ))}
        <div style={{ flex: '1 1 140px', maxWidth: 200, padding: '12px 14px', borderRadius: 10, background: 'var(--bg-muted)', border: '1px solid var(--border-strong)', display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>Total</span>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{data.length} collab.</span>
            <span style={{ fontWeight: 700, fontSize: 15 }}>{totalLeads} leads · {totalAujourdhui} auj. · {totalHier} hier</span>
          </div>
        </div>
      </div>

      {sections.map(section => {
        const isCollapsed = collapsed[section.key];
        return (
          <div key={section.key} style={{ marginBottom: 12 }}>
            <div onClick={() => setCollapsed(prev => ({ ...prev, [section.key]: !prev[section.key] }))}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: isCollapsed ? 10 : '10px 10px 0 0', cursor: 'pointer', userSelect: 'none' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{isCollapsed ? '▶' : '▼'}</span>
              <span style={{ fontSize: 16 }}>{section.icon}</span>
              <span style={{ fontWeight: 700, fontSize: 14 }}>{section.label}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>({section.entries.length})</span>
              <span style={{ marginLeft: 'auto', fontWeight: 600, fontSize: 13, color: section.totalLeads > 10 ? 'light-dark(#b91c1c, var(--color-error-dark))' : 'var(--text-secondary)' }}>{section.totalLeads} leads</span>
            </div>
            {!isCollapsed && (
              <div className="table-container" style={{ borderRadius: '0 0 10px 10px', borderTop: 'none' }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ ...thBase, textAlign: 'left' }} onClick={() => handleSort('collaborateur')}>Collaborateur{sortIcon('collaborateur')}</th>
                      <th style={{ ...thBase, textAlign: 'center' }} onClick={() => handleSort('total_leads')}>Leads à traiter{sortIcon('total_leads')}</th>
                      <th style={{ ...thBase, textAlign: 'center' }} onClick={() => handleSort('traites_aujourdhui')}>Traités aujourd'hui{sortIcon('traites_aujourdhui')}</th>
                      <th style={{ ...thBase, textAlign: 'center' }} onClick={() => handleSort('traites_hier')}>Traités hier{sortIcon('traites_hier')}</th>
                      <th style={{ ...thBase, textAlign: 'center' }} onClick={() => handleSort('traites_7j')}>Traités 7j{sortIcon('traites_7j')}</th>
                      <th style={{ ...thBase, textAlign: 'left' }} onClick={() => handleSort('dernier_traitement')}>Dernier traitement{sortIcon('dernier_traitement')}</th>
                    </tr>
                  </thead>
                  <tbody>{sortRows(section.entries).map(renderRow)}</tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {data.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Aucun collaborateur avec des leads en cours</div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Configuration des vendeurs (marques autorisées par type VN/VO + flag absent)
// ────────────────────────────────────────────────────────────────────────────
function ConfigVendeursLeadsWeb({ user, concessionsList = [] }) {
  const [team, setTeam] = useState([]);
  const [marquesRef, setMarquesRef] = useState([]); // [{ id, nom, concessions: [{id, nom}] }]
  const [loading, setLoading] = useState(true);
  const [savingAbsent, setSavingAbsent] = useState({}); // { [userId]: true }
  const [savingMarque, setSavingMarque] = useState({}); // { [`${userId}|${VN|VO}|${nom}`]: true }
  // Filtre concession : utile au super-admin qui voit tous les vendeurs ; ignoré
  // si l'utilisateur est rattaché à une seule concession (déjà filtré côté backend).
  const [filterConcession, setFilterConcession] = useState('');
  const canFilterConcession = user?.role === 'super-admin' && !user?.concession_id;

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [t, m] = await Promise.all([
        leadsWebApi.getConfigTeam(),
        referentielsApi.getMarques(),
      ]);
      setTeam(t.data?.users || []);
      setMarquesRef((m.data?.marques || m.data || []).filter(x => x.actif !== false));
    } catch (err) {
      console.error('Erreur chargement config vendeurs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const toggleAbsent = async (u, value) => {
    setSavingAbsent(s => ({ ...s, [u.id]: true }));
    try {
      await leadsWebApi.setVendeurAbsent(u.id, value);
      setTeam(arr => arr.map(x => x.id === u.id ? { ...x, absent: value } : x));
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingAbsent(s => ({ ...s, [u.id]: false }));
    }
  };

  // Liste des marques disponibles pour un vendeur = celles rattachées à sa
  // concession (via marques_concessions, exposé par /referentiels/marques
  // dans `concessions[]`). Pas de concession → toutes les marques.
  const availableMarquesFor = (u) => (
    u.concession_id
      ? marquesRef.filter(m => (m.concessions || []).some(c => c.id === u.concession_id))
      : marquesRef
  );

  // Toggle VN d'une marque sur un vendeur. Sémantique :
  //   - DB marques_vn vide      = pas de restriction (vendeur reçoit TOUTES les marques)
  //   - DB marques_vn non vide  = liste blanche, uniquement ces marques
  // Sur clic : ajout ou retrait simple de la liste explicite.
  const toggleMarque = async (u, marqueName) => {
    const current = u.marques_vn || [];
    const next = current.includes(marqueName)
      ? current.filter(n => n !== marqueName)
      : [...current, marqueName];
    const cellKey = `${u.id}|VN|${marqueName}`;
    setSavingMarque(s => ({ ...s, [cellKey]: true }));
    try {
      await leadsWebApi.setVendeurMarques(u.id, 'VN', next);
      setTeam(arr => arr.map(x => x.id === u.id ? { ...x, marques_vn: next } : x));
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSavingMarque(s => ({ ...s, [cellKey]: false }));
    }
  };

  const renderMarquesCell = (u) => {
    const hasVN = (u.types_leads_web || []).includes('VN');
    if (!hasVN) {
      return <span style={{ color: 'var(--text-muted)' }}>—</span>;
    }
    const available = availableMarquesFor(u);
    if (available.length === 0) {
      return (
        <span style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 12 }}>
          Aucune marque rattachée à cette concession
        </span>
      );
    }
    const explicit = u.marques_vn || [];
    // Vert si la marque est explicitement dans la liste. Sinon gris.
    // Liste vide = aucun pill vert = vendeur reçoit TOUTES les marques (no restriction).
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
        {available.map(m => {
          const isOn = explicit.includes(m.nom);
          const cellKey = `${u.id}|VN|${m.nom}`;
          const saving = !!savingMarque[cellKey];
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => toggleMarque(u, m.nom)}
              disabled={saving}
              style={{
                padding: '3px 10px', fontSize: 12, borderRadius: 12,
                border: '1px solid', fontWeight: 500, whiteSpace: 'nowrap',
                cursor: saving ? 'wait' : 'pointer',
                background:  isOn ? 'var(--color-success-light)' : 'var(--bg-muted)',
                color:       isOn ? 'var(--color-success)'       : 'var(--text-muted)',
                borderColor: isOn ? 'var(--color-success)'       : 'var(--border-default)',
                transition: 'all 120ms ease',
                opacity: saving ? 0.6 : 1,
              }}
              title={isOn ? `Cliquer pour désactiver « ${m.nom} »` : `Cliquer pour activer « ${m.nom} »`}
            >
              {m.nom}
            </button>
          );
        })}
        {explicit.length === 0 && (
          <span style={{
            marginLeft: 6, fontSize: 11, fontStyle: 'italic',
            color: 'var(--text-muted)',
          }}>
            (toutes les marques)
          </span>
        )}
      </div>
    );
  };

  const renderAbsentButton = (u) => {
    const isAbsent = !!u.absent;
    return (
      <button
        onClick={() => toggleAbsent(u, !isAbsent)}
        disabled={savingAbsent[u.id]}
        style={{
          padding: '5px 14px', fontSize: 13, fontWeight: 600,
          borderRadius: 16, border: '1px solid',
          cursor: savingAbsent[u.id] ? 'wait' : 'pointer',
          background: isAbsent ? 'var(--color-error-light)' : 'var(--color-success-light)',
          color:      isAbsent ? 'var(--color-error)'       : 'var(--color-success)',
          borderColor:isAbsent ? 'var(--color-error)'       : 'var(--color-success)',
          minWidth: 92,
        }}
        title={isAbsent ? 'Cliquer pour marquer présent' : 'Cliquer pour marquer absent'}
      >
        {isAbsent ? 'Absent' : 'Présent'}
      </button>
    );
  };

  return (
    <div style={{ padding: '12px 0' }}>
      <div style={{
        padding: '10px 14px', marginBottom: 14, borderRadius: 8,
        background: 'var(--color-warning-light)',
        border: '1px solid var(--color-warning)',
        color: 'light-dark(var(--color-warning), var(--color-warning-dark))', fontSize: 13,
      }}>
        ⚠️ <strong>Cette page n'est utile que si l'affectation automatique des leads est activée</strong> pour votre concession
        (case « Affectation automatique des leads web » dans Administration → Concessions).
        Sinon les réglages saisis ici n'ont aucun effet.
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
        Cliquez sur une marque pour l'activer (<strong style={{ color: 'light-dark(var(--color-success), var(--color-success-dark))' }}>vert</strong>) ou la désactiver (gris).
        Aucune marque activée = pas de restriction (le vendeur reçoit toutes les marques pour ce type).
        Le bouton <strong>Présent / Absent</strong> suspend l'affectation automatique tant qu'il est sur « Absent ».
      </div>

      {canFilterConcession && concessionsList.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <label style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Filtrer par concession :</label>
          <select
            className={`page-select${filterConcession ? ' active-filter' : ''}`}
            value={filterConcession}
            onChange={(e) => setFilterConcession(e.target.value)}
            style={{ minWidth: 220 }}
          >
            <option value="">Toutes les concessions</option>
            {concessionsList.map(c => (
              <option key={c.id} value={c.id}>{c.nom}</option>
            ))}
          </select>
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>Chargement…</div>
      )}
      {!loading && team.length === 0 && (
        <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-secondary)' }}>
          Aucun vendeur configuré avec un type de lead (VN / VO).
        </div>
      )}

      {/* ── Groupe Leads VN — marques configurables ────────────────────── */}
      {!loading && (() => {
        const vnList = team
          .filter(u => (u.types_leads_web || []).includes('VN'))
          .filter(u => !filterConcession || String(u.concession_id) === String(filterConcession));
        if (vnList.length === 0) return null;
        return (
          <div style={{ marginBottom: 18 }}>
            <h3 style={{ margin: '12px 0 8px', fontSize: 14, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              🚗 Leads VN — {vnList.length} vendeur{vnList.length > 1 ? 's' : ''}
            </h3>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Collaborateur</th>
                    <th>Concession</th>
                    <th>Marques</th>
                    <th style={{ textAlign: 'center' }}>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {vnList.map(u => (
                    <tr key={u.id}>
                      <td><strong>{u.nom}</strong> {u.prenom}</td>
                      <td>{u.concession_nom || '—'}</td>
                      <td>{renderMarquesCell(u)}</td>
                      <td style={{ textAlign: 'center' }}>{renderAbsentButton(u)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ── Groupe Leads VO — toutes marques (pas de config par marque) ──── */}
      {!loading && (() => {
        const voList = team
          .filter(u => (u.types_leads_web || []).includes('VO'))
          .filter(u => !filterConcession || String(u.concession_id) === String(filterConcession));
        if (voList.length === 0) return null;
        return (
          <div>
            <h3 style={{ margin: '12px 0 8px', fontSize: 14, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              🔄 Leads VO — {voList.length} vendeur{voList.length > 1 ? 's' : ''}
            </h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6, fontStyle: 'italic' }}>
              Les vendeurs VO reçoivent toutes les marques par nature (pas de filtre par marque).
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Collaborateur</th>
                    <th>Concession</th>
                    <th style={{ textAlign: 'center' }}>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {voList.map(u => (
                    <tr key={u.id}>
                      <td><strong>{u.nom}</strong> {u.prenom}</td>
                      <td>{u.concession_nom || '—'}</td>
                      <td style={{ textAlign: 'center' }}>{renderAbsentButton(u)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

    </div>
  );
}

export default LeadsView;
